import { useCallback, useMemo, useState } from "react";

import Action from "utils/history/Action";

export type useHistoryProps<T> = {
	initialState: T;
};

export type History<T> = {
	state: T;
	actions: Array<Action>;

	hasPrevious: boolean;
	hasNext: boolean;

	reset: (state: T) => void;
	perform: (action: Action) => void;
	undo: () => void;
	redo: () => void;
};

type HistoryState<T> = {
	state: T;
	actions: Array<Action>;
	index: number;
};

export default function useHistory<T>(
	props: Readonly<useHistoryProps<T>>
): History<T> {
	const [state, setState] = useState<HistoryState<T>>({
		state: props.initialState,
		actions: [],
		index: 0
	});

	const hasPrevious = useMemo(() => state.index > 0, [state]);
	const hasNext = useMemo(() => state.index < state.actions.length, [state]);

	const reset = useCallback(
		(newState: T): void => {
			setState({
				state: newState,
				actions: [],
				index: 0
			});
		},
		[setState]
	);

	const perform = useCallback(
		(action: Action): void => {
			setState(previous => ({
				state: action.apply(previous.state),
				actions: previous.actions.toSpliced(
					previous.index,
					previous.actions.length - previous.index,
					action
				),
				index: previous.index + 1
			}));
		},
		[setState]
	);

	const undo = useCallback((): void => {
		if (!hasPrevious) {
			return;
		}

		setState(previous => ({
			state: previous.actions[previous.index - 1].revert(previous.state),
			actions: previous.actions,
			index: previous.index - 1
		}));
	}, [state.index, setState]);

	const redo = useCallback((): void => {
		if (!hasNext) {
			return;
		}

		setState(previous => ({
			state: previous.actions[previous.index].apply(previous.state),
			actions: previous.actions,
			index: previous.index + 1
		}));
	}, [state.index, setState]);

	return {
		state: state.state,
		actions: state.actions.slice(0, state.index),

		hasPrevious,
		hasNext,

		reset,
		perform,
		undo,
		redo
	};
}
